import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { loginRedirect } from '@svenskaspelab/nova-core/nextjs/auth';
import { useIsCouponWagerPending } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/coupon';
import type { CouponStatus } from '@svenskaspelab/poolbetting/schema/coupon';
import { Betslip, BetslipHedges, BetslipLabel, BetslipPayButton, BetslipPrice, BetslipProgressBar } from '@svenskaspelab/poolbetting/ui/Betslip';
import { ShowHideButton } from '@svenskaspelab/poolbetting/ui/ShowHideButton';
import Balance from '@svenskaspelab/ui/react/icons/Balance';
import { type ReactNode, useEffect, useRef } from 'react';
const handleOpenDrawerClick = (): void => {
  trackEvent({
    category: 'Nya tipsen',
    action: 'Coupon overview',
    optLabel: 'Powerplay'
  });
};
type BetslipStandardProps = {
  betslipProps: {
    isPopulatedCoupon: boolean;
    isMultiCoupon: boolean;
    totalCouponPrice: string;
    currentCouponPrice: string;
    fullHedgeCount: number;
    halfHedgeCount: number;
    couponEventsStarted: number;
    couponEventCount: number;
    couponStatus: CouponStatus;
    hasAdditions: boolean;
    betslipContent: ReactNode;
    balance: string;
    shouldRedirectUser?: boolean;
    returnUrl?: string;
  };
};

/**
 * Component för standard betslip.
 *
 * @param root0 - The props object.
 * @param root0.betslipProps - Correct props for the betslip standard component.
 * @returns standard betslip component
 */
function BetslipStandard(t0) {
  const $ = _c(32);
  const {
    betslipProps
  } = t0;
  const {
    totalCouponPrice,
    currentCouponPrice,
    isMultiCoupon,
    isPopulatedCoupon,
    fullHedgeCount,
    halfHedgeCount,
    couponEventsStarted,
    couponEventCount,
    couponStatus,
    hasAdditions,
    betslipContent,
    balance,
    shouldRedirectUser,
    returnUrl
  } = betslipProps;
  const isPopulatedCouponRef = useRef(isPopulatedCoupon);
  const couponStatusRef = useRef(couponStatus);
  let t1;
  let t2;
  if ($[0] !== couponStatus || $[1] !== isPopulatedCoupon) {
    t1 = () => {
      if (isPopulatedCoupon !== isPopulatedCouponRef.current) {
        if (!isPopulatedCouponRef.current) {
          trackEvent({
            category: "Wager",
            action: "Betslip started",
            optLabel: "Powerplay"
          });
        }
        isPopulatedCouponRef.current = isPopulatedCoupon;
      }
      if (couponStatus !== couponStatusRef.current) {
        if (couponStatus === "VALID" && couponStatusRef.current !== "VALID") {
          trackEvent({
            category: "Wager",
            action: "Betslip complete",
            optLabel: "Powerplay"
          });
        }
        couponStatusRef.current = couponStatus;
      }
    };
    t2 = [isPopulatedCoupon, couponStatus];
    $[0] = couponStatus;
    $[1] = isPopulatedCoupon;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const isPending = useIsCouponWagerPending();
  let t3;
  if ($[4] !== returnUrl || $[5] !== shouldRedirectUser) {
    t3 = shouldRedirectUser ? () => {
      loginRedirect({
        returnUrl,
        cancelUrl: returnUrl
      });
    } : undefined;
    $[4] = returnUrl;
    $[5] = shouldRedirectUser;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleBalanceClick = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["[grid-template-areas:'label_hedges_price''payButton_payButton_payButton']", "grid-cols-[min-content_auto_minmax(40px,_80px)]", "lg:[grid-template-areas:'label_hedges_balance_price_payButton']", "lg:grid-cols-[max-content_auto_minmax(60px,_120px)_minmax(40px,_80px)_minmax(154px,_250px)]"];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let betslipClassNames = t4;
  const shouldShowProgressBar = couponStatus !== "VALID";
  if (shouldShowProgressBar) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = ["[grid-template-areas:'label_hedges_price''progressBar_progressBar_progressBar']", "grid-cols-[min-content_auto_minmax(40px,_80px)]", "lg:[grid-template-areas:'label_hedges_progressBar_price']", "lg:grid-cols-[max-content_max-content_auto_minmax(40px,_80px)]"];
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    betslipClassNames = t5;
  }
  const betslipLabel = isMultiCoupon ? `Standard: ${currentCouponPrice}` : "Standard";
  const t5 = betslipClassNames.join(" ");
  let t6;
  if ($[9] !== betslipLabel) {
    t6 = <BetslipLabel label={betslipLabel} />;
    $[9] = betslipLabel;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== fullHedgeCount || $[12] !== halfHedgeCount) {
    t7 = <BetslipHedges halfHedgeCount={halfHedgeCount} fullHedgeCount={fullHedgeCount} />;
    $[11] = fullHedgeCount;
    $[12] = halfHedgeCount;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== balance || $[15] !== couponEventCount || $[16] !== couponEventsStarted || $[17] !== handleBalanceClick || $[18] !== hasAdditions || $[19] !== isPending || $[20] !== shouldShowProgressBar) {
    t8 = shouldShowProgressBar ? <BetslipProgressBar maxValue={couponEventCount} currentValue={couponEventsStarted} /> : <><ShowHideButton className="text-layer-fg-muted hidden font-normal [grid-area:balance] lg:grid lg:grid-cols-[max-content_auto]" showValue={balance} hideValue="Visa saldo" variant="ghost" onClick={handleBalanceClick} Icon={Balance} /><BetslipPayButton hasAdditions={hasAdditions} disabled={isPending} /></>;
    $[14] = balance;
    $[15] = couponEventCount;
    $[16] = couponEventsStarted;
    $[17] = handleBalanceClick;
    $[18] = hasAdditions;
    $[19] = isPending;
    $[20] = shouldShowProgressBar;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== totalCouponPrice) {
    t9 = <BetslipPrice price={totalCouponPrice} />;
    $[22] = totalCouponPrice;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== betslipContent || $[25] !== isPopulatedCoupon || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = <Betslip className={t5} isPopulatedCoupon={isPopulatedCoupon} betslipContent={betslipContent} onOpenDrawerClick={handleOpenDrawerClick}>{t6}{t7}{t8}{t9}</Betslip>;
    $[24] = betslipContent;
    $[25] = isPopulatedCoupon;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
export default BetslipStandard;